import {
  AddMyFleetVesselsResponse,
  MyFleetVessels,
} from '../models/my-fleet.model';
import { chunkArray, runWithConcurrency } from '../utils/batchHelpers';
import { addMyFleetVessels } from './my-fleet';

/**
 * Batch create vessels with progress and error collection.
 *
 * @param vessels - Array of vessels to add.
 * @param batchSize - Batch size for vessel creation.
 * @param concurrency - Concurrency limit for vessel creation.
 * @param onProgress - Optional callback for progress updates: (completed, total, failed) => void
 * @param onBatchError - Optional callback for batch errors.
 * @returns {Promise<{ batchResults: AddMyFleetVesselsResponse[], errorOccurred: boolean, batchErrors: any[] }>} Results and errors.
 */

interface BatchCreateVesselsOptions {
  vessels: { imo: number; emails?: string[] }[];
  batchSize: number;
  concurrency: number;
  setProgress?: (progress: {
    completed: number;
    total: number;
    failed: number;
  }) => void;
  setErrors?: (errors: any[]) => void;
  setResponse?: (response: AddMyFleetVesselsResponse) => void;
}

async function batchCreateVessels({
  vessels,
  batchSize,
  concurrency,
  setProgress,
  setErrors,
  setResponse,
}: BatchCreateVesselsOptions): Promise<AddMyFleetVesselsResponse> {
  const vesselBatches = chunkArray(vessels, batchSize);
  const batchCreateResults: AddMyFleetVesselsResponse[] = [];
  let errorOccurred = false;
  let completed = 0;
  let failed = 0;
  let batchCreateErrors: any[] = [];

  const tasks = vesselBatches.map((batch, idx) => async () => {
    if (setProgress) setProgress({ completed, total: vessels.length, failed });
    const vesselsToAdd: MyFleetVessels = batch.map((v) => ({
      imo: String(v.imo),
    }));
    try {
      const vesselCreateResponse = await addMyFleetVessels(vesselsToAdd);
      batchCreateResults[idx] = vesselCreateResponse;
      completed += batch.length;
      if (setProgress)
        setProgress({ completed, total: vessels.length, failed });
      if (
        vesselCreateResponse.errors &&
        vesselCreateResponse.errors.length > 0
      ) {
        errorOccurred = true;
        batchCreateErrors = [
          ...batchCreateErrors,
          ...(vesselCreateResponse.errors ?? []),
        ];
        if (setErrors) setErrors(batchCreateErrors);
      }
      if (setResponse) setResponse(vesselCreateResponse);
      return vesselCreateResponse;
    } catch (error: any) {
      errorOccurred = true;
      failed += 1;
      if (setProgress)
        setProgress({ completed, total: vessels.length, failed });
      const errList = error?.response?.data?.errors
        ? error.response.data.errors
        : [{ message: error?.message || 'Batch vessel creation failed.' }];
      batchCreateErrors = [...batchCreateErrors, ...errList];
      if (setErrors) setErrors(batchCreateErrors);
      if (setResponse)
        setResponse({
          message: error?.message || 'Batch vessel creation failed.',
        } as AddMyFleetVesselsResponse);
      return { message: error?.message || 'Batch vessel creation failed.' };
    }
  });

  await runWithConcurrency(tasks, concurrency);

  // Aggregate all vessels and errors
  const allVessels = batchCreateResults.flatMap((res) => res?.vessels || []);
  const message = errorOccurred
    ? 'Errors raised during vessel creation:'
    : 'Successfully created vessel(s)';
  const result: AddMyFleetVesselsResponse = {
    message,
    vessels: allVessels,
    errors: batchCreateErrors,
  };
  if (setResponse) setResponse(result);
  return result;
}

export default batchCreateVessels;
